import { useEffect, useRef, useState } from 'preact/hooks';
import { resizeImageToDataURL } from '../lib/resizeImage';

/**
 * Photo edit controls for the Recipe Detail page.
 *
 * Desktop (hover-capable): a translucent floating control panel in the
 * bottom-right of the photo, fades in on hover of the wrapping element.
 * Mobile (no hover): a small ✎ icon button in the bottom-right corner;
 * tap opens a slide-up bottom sheet with the same actions.
 *
 * The component is rendered absolutely-positioned inside the photo wrap
 * (`.detail-photo-wrap`). The wrap controls hover gating via CSS.
 */
export function RecipePhotoEditor(props: {
  imageUrl: string | null;
  onChange: (url: string | null) => Promise<void>;
  focusMode?: boolean;
  onToggleFocusMode?: () => void;
}) {
  const fileRef = useRef<HTMLInputElement>(null);
  const sheetFileRef = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [urlInput, setUrlInput] = useState('');
  const [showUrl, setShowUrl] = useState(false);
  const [sheetOpen, setSheetOpen] = useState(false);
  const [sheetUrlMode, setSheetUrlMode] = useState(false);

  useEffect(() => {
    if (!error) return;
    const t = setTimeout(() => setError(null), 4000);
    return () => clearTimeout(t);
  }, [error]);

  useEffect(() => {
    if (!props.focusMode) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') props.onToggleFocusMode?.(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [props.focusMode]);

  async function handleFile(e: Event) {
    const input = e.target as HTMLInputElement;
    const file = input.files?.[0];
    if (!file) return;
    setError(null);
    setBusy(true);
    try {
      const dataUrl = await resizeImageToDataURL(file);
      const MAX_DATA_URL_BYTES = 200_000;
      const approxBytes = dataUrl.length * 0.75; // base64 expansion factor ~4/3
      if (approxBytes > MAX_DATA_URL_BYTES) {
        setError('That photo is too large even after resizing. Try a smaller or simpler image.');
        return;
      }
      await props.onChange(dataUrl);
      setSheetOpen(false);
    } catch (err) {
      setError((err as Error).message || 'Could not load image');
    } finally {
      setBusy(false);
      if (fileRef.current) fileRef.current.value = '';
      if (sheetFileRef.current) sheetFileRef.current.value = '';
    }
  }

  async function handleUrlSave() {
    const trimmed = urlInput.trim();
    if (!trimmed) return;
    setError(null);
    setBusy(true);
    try {
      const ok = await new Promise<boolean>(resolve => {
        const img = new Image();
        img.onload = () => resolve(true);
        img.onerror = () => resolve(false);
        img.src = trimmed;
        setTimeout(() => resolve(false), 5000);
      });
      if (!ok) {
        setError('That URL did not load as an image. Double-check it.');
        return;
      }
      await props.onChange(trimmed);
      setUrlInput('');
      setShowUrl(false);
      setSheetUrlMode(false);
      setSheetOpen(false);
    } catch (err) {
      setError((err as Error).message || 'Could not save URL');
    } finally {
      setBusy(false);
    }
  }

  async function handleClear() {
    setBusy(true);
    setError(null);
    try {
      await props.onChange(null);
      setSheetOpen(false);
    } catch (err) {
      setError((err as Error).message || 'Could not remove photo');
    } finally {
      setBusy(false);
    }
  }

  const hasImage = !!props.imageUrl;

  return (
    <>
      {/* Single edit-photo button that opens the action menu (sheet).
          Hidden during focus mode so the user can drag without it in the way. */}
      {!props.focusMode && (
        <button
          type="button"
          className="detail-photo-edit-icon"
          onClick={() => setSheetOpen(true)}
          aria-label="Edit photo"
          disabled={busy}
        >
          ✎
        </button>
      )}
      {/* While in focus mode, surface a small floating "Done" button so the user
          has an explicit exit path beyond pressing ESC. */}
      {props.focusMode && props.onToggleFocusMode && (
        <button
          type="button"
          className="detail-photo-edit-icon detail-photo-edit-icon-done"
          onClick={props.onToggleFocusMode}
          aria-label="Done repositioning"
        >
          Done
        </button>
      )}

      <input
        ref={fileRef}
        type="file"
        accept="image/*"
        onChange={handleFile}
        style={{ display: 'none' }}
      />

      {busy && (
        <div className="detail-photo-busy">
          <span>Saving…</span>
        </div>
      )}

      {error && <div className="detail-photo-error">{error}</div>}

      {/* Mobile bottom sheet */}
      {sheetOpen && (
        <>
          <div
            className="detail-photo-sheet-backdrop"
            onClick={() => { setSheetOpen(false); setSheetUrlMode(false); }}
          />
          <div className="detail-photo-sheet" role="dialog" aria-label="Edit photo">
            {!sheetUrlMode ? (
              <>
                <button
                  type="button"
                  className="detail-photo-sheet-btn primary"
                  onClick={() => sheetFileRef.current?.click()}
                  disabled={busy}
                >
                  📷 Upload photo
                </button>
                <button
                  type="button"
                  className="detail-photo-sheet-btn"
                  onClick={() => setSheetUrlMode(true)}
                  disabled={busy}
                >
                  🔗 Paste URL
                </button>
                {hasImage && props.onToggleFocusMode && (
                  <button
                    type="button"
                    className="detail-photo-sheet-btn"
                    onClick={() => { setSheetOpen(false); props.onToggleFocusMode!(); }}
                    disabled={busy}
                  >
                    🎯 Reposition photo
                  </button>
                )}
                {hasImage && (
                  <button
                    type="button"
                    className="detail-photo-sheet-btn ghost"
                    onClick={handleClear}
                    disabled={busy}
                  >
                    Remove photo
                  </button>
                )}
                <button
                  type="button"
                  className="detail-photo-sheet-btn cancel"
                  onClick={() => setSheetOpen(false)}
                >
                  Cancel
                </button>
              </>
            ) : (
              <>
                <input
                  type="url"
                  placeholder="https://example.com/photo.jpg"
                  value={urlInput}
                  onInput={e => setUrlInput((e.target as HTMLInputElement).value)}
                  className="detail-photo-sheet-input"
                />
                <button
                  type="button"
                  className="detail-photo-sheet-btn primary"
                  onClick={handleUrlSave}
                  disabled={busy || !urlInput.trim()}
                >
                  Save URL
                </button>
                <button
                  type="button"
                  className="detail-photo-sheet-btn cancel"
                  onClick={() => { setSheetUrlMode(false); setUrlInput(''); }}
                >
                  Back
                </button>
              </>
            )}
            <input
              ref={sheetFileRef}
              type="file"
              accept="image/*"
              onChange={handleFile}
              style={{ display: 'none' }}
            />
          </div>
        </>
      )}
    </>
  );
}
